import { useTranslation } from 'react-i18next';

import { useNotify } from '@hotjar/hotsauce';
import { useUpsellCheckout } from '@hotjar/webapp/domains/revenue/hooks/useUpsellCheckout';
import type {
  SubmitCheckoutParams,
  SubmitCheckoutResult,
} from '@hotjar/webapp/domains/revenue/hooks/useUpsellCheckout';
import { logError } from '@hotjar/insights-common/lib/logger';
import { createGlobalModal } from '@hotjar/webapp/features/GlobalModal/createGlobalModal';
import type { GlobalModalProps } from '@hotjar/webapp/features/GlobalModal/createGlobalModal';
import type { SiteAccount } from '@hotjar/account';
import { useBillingPeriodState } from '@hotjar/upsell';
import type { BillingPeriod } from '@hotjar/subscription-management';
import { bus, upgradeRequestSent } from '@hotjar/webapp/utils/events';

interface ModalComponent<TProps, TSubmit, TCancel = Error> {
  (
    props: TProps &
      GlobalModalProps<TSubmit, TCancel> & {
        billingPeriod: BillingPeriod;
        billingPeriodChanged: boolean;
        setBillingPeriod: (value: BillingPeriod) => unknown;
      },
  ): JSX.Element | null;
}

/**
 * This is a wrapper on top of `createGlobalModal`. It wraps it with some extra functionality that will likely
 * be the same for all upsell modals.
 *
 * It accepts a React component in the props, and assumes that the component will have an `account` prop that is of
 * type `SiteAccount`. It needs this to determine things like:
 *   - Billing 3.0 support
 *   - If the user is the owner of the account
 *
 * FIXME: There's a type caveat. Currently, this function says it returns a promise that resolves to `TSubmit` but
 * that's not true. In reality, the promise resolves to `void`. See details in the implementation below.
 * It's not easy to fix this without refactoring `createGlobalModal` extensively though.
 */
export const createGlobalUpsellModal = <TProps, TSubmit, TCancel = Error>(
  ModalComponent: ModalComponent<TProps, TSubmit, TCancel>,
): ((
  modalComponentProps: TProps & {
    account: Pick<SiteAccount, 'adminIds' | 'id' | 'features' | 'billingCycle'>;
  },
) => Promise<TSubmit>) => {
  return createGlobalModal<
    TProps,
    TSubmit,
    TCancel,
    { account: Pick<SiteAccount, 'adminIds' | 'id' | 'features' | 'billingCycle'> }
  >((props) => {
    const { account } = props;

    const { t } = useTranslation('revenue');
    const { showToast } = useNotify();
    const {
      value: billingPeriod,
      isChanged: billingPeriodChanged,
      setValue: setBillingPeriod,
    } = useBillingPeriodState(account);
    const { submitCheckout } = useUpsellCheckout({
      account,
      newBillingPeriod: billingPeriodChanged ? billingPeriod : undefined,
    });

    const onSubmit = async (params: SubmitCheckoutParams): Promise<SubmitCheckoutResult> => {
      try {
        const result = await submitCheckout(params);

        if (result.success && result.isUpgradeRequest) {
          bus.publish(upgradeRequestSent());
        }

        /**
         * This is the original `onSubmit` callback defined in `createGlobalModal` that will resolve the promise
         * returned by the function. We need to call this so that the additional React instance created by
         * `createGlobalModal` is cleaned up, and any pending promise is resolved. The resolved `undefined` value
         * goes back to wherever the function returned by `createGlobalUpsellModal` was called, for example the
         * `upsell` entry point function.
         *
         * This is why/where we resolve the original promise with `void`.
         */
        props.onSubmit();

        /**
         * Resolve our custom `onSubmit` promise. The resolved value will go back to wherever `onSubmit` was called,
         * for example the `PlanColumn` component.
         */
        return result;
      } catch (error) {
        if (error && error instanceof Error) {
          logError(error);
        }

        showToast({
          kind: 'danger',
          text: t('upsell.plan-upsell.error'),
        });

        return {
          success: false,
          upsellFlowFinished: false,
        };
      }
    };

    return (
      <ModalComponent
        {...props}
        onSubmit={onSubmit}
        billingPeriod={billingPeriod}
        billingPeriodChanged={billingPeriodChanged}
        setBillingPeriod={setBillingPeriod}
      />
    );
  });
};
